import {
  alpha,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Fab,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Toolbar,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { Add, Diversity3, Edit, DeleteOutline } from "@mui/icons-material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { AuthState } from "store/features/auth";
import { RootState } from "store";
import { isNurseryAdmin } from "utils/roles";
import { getClassrooms } from "apis";
import ClassroomDialog from "components/ClassroomDialog";
import DeleteClassroomDialog from "components/DeleteClassroomDialog";
import TablePaginationLocalizationProvider from "components/utils/TablePaginationLocalizationProvider";
import { formatAgeMonths } from "utils";

type Classroom = {
  id: number;
  name: string;
  description: string;
  rangeFrom: number;
  rangeTo: number;
  childrenCount?: number;
  staffCount?: number;
};

type SortKey = "name" | "range";
type SortDir = "asc" | "desc";

const ClassroomsPage: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();

  const { role } = useSelector<RootState, AuthState>((store) => store.auth);
  const canMutate = isNurseryAdmin(role);

  const [classrooms, setClassrooms] = useState<Classroom[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortKey, setSortKey] = useState<SortKey>("name");
  const [sortDir, setSortDir] = useState<SortDir>("asc");

  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [selectedItem, setSelectedItem] = useState<Classroom>();

  const fetchData = useCallback(() => {
    setLoading(true);
    getClassrooms()
      .then((data) => setClassrooms(data || []))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDialogClose = useCallback(() => {
    setOpenAdd(false);
    setOpenEdit(false);
    fetchData();
  }, [fetchData]);

  const handleDeleteClose = useCallback(
    (deleted?: boolean) => {
      setOpenDelete(false);
      if (deleted) fetchData();
    },
    [fetchData],
  );

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const sorted = useMemo(() => {
    const list = classrooms.slice();
    const dir = sortDir === "asc" ? 1 : -1;
    list.sort((a, b) => {
      if (sortKey === "name") return dir * a.name.localeCompare(b.name);
      if (sortKey === "range") return dir * (a.rangeFrom - b.rangeFrom);
      return 0;
    });
    return list;
  }, [classrooms, sortKey, sortDir]);

  return (
    <>
      <Toolbar
        sx={{
          bgcolor: "background.paper",
          borderBottom: 1,
          borderColor: "divider",
          gap: 1.5,
        }}
      >
        <Diversity3 sx={{ color: "primary.main", fontSize: 24 }} />
        <Typography variant="h6" fontWeight={600}>
          {t("classroom.label")}
        </Typography>
        <Chip
          label={classrooms.length}
          size="small"
          sx={{
            bgcolor: alpha(theme.palette.text.primary, 0.06),
            fontWeight: 600,
          }}
        />
      </Toolbar>

      {loading ? (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      ) : (
        <Container sx={{ py: 2 }}>
          {classrooms.length === 0 ? (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              py={8}
              color="text.secondary"
            >
              <Diversity3
                sx={{ fontSize: 64, mb: 2, color: "action.disabled" }}
              />
              <Typography variant="h6" gutterBottom>
                {t("classroom.empty")}
              </Typography>
              {canMutate && (
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => setOpenAdd(true)}
                  sx={{ mt: 2, textTransform: "none", fontWeight: 600 }}
                >
                  {t("classroom.add")}
                </Button>
              )}
            </Box>
          ) : (
            <Card
              variant="outlined"
              sx={{ borderRadius: 3, overflow: "hidden" }}
            >
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow
                      sx={{ bgcolor: alpha(theme.palette.primary.main, 0.04) }}
                    >
                      <TableCell sx={{ fontWeight: 700 }}>
                        <TableSortLabel
                          active={sortKey === "name"}
                          direction={sortKey === "name" ? sortDir : "asc"}
                          onClick={() => handleSort("name")}
                        >
                          {t("inputs.class_name")}
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>
                        {t("inputs.description")}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>
                        <TableSortLabel
                          active={sortKey === "range"}
                          direction={sortKey === "range" ? sortDir : "asc"}
                          onClick={() => handleSort("range")}
                        >
                          {t("inputs.age_group")}
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="center">
                        {t("children.label")}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="center">
                        {t("staff.label")}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="center">
                        {t("labels.action")}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sorted
                      .slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage,
                      )
                      .map((classroom) => (
                        <TableRow key={classroom.id} hover>
                          <TableCell>
                            <Box display="flex" alignItems="center" gap={1.5}>
                              <Box
                                sx={{
                                  width: 36,
                                  height: 36,
                                  borderRadius: 2,
                                  bgcolor: alpha(
                                    theme.palette.primary.main,
                                    0.08,
                                  ),
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  color: "primary.main",
                                  fontWeight: 700,
                                  fontSize: 14,
                                }}
                              >
                                {classroom.name[0]}
                              </Box>
                              <Typography variant="body2" fontWeight={500}>
                                {classroom.name}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {classroom.description || "—"}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={`${formatAgeMonths(classroom.rangeFrom, t)} — ${formatAgeMonths(classroom.rangeTo, t)}`}
                              size="small"
                              variant="outlined"
                              sx={{ fontWeight: 500 }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={classroom.childrenCount ?? 0}
                              size="small"
                              color={
                                (classroom.childrenCount ?? 0) > 0
                                  ? "primary"
                                  : "default"
                              }
                              variant={
                                (classroom.childrenCount ?? 0) > 0
                                  ? "filled"
                                  : "outlined"
                              }
                              sx={{ fontWeight: 600, minWidth: 36 }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={classroom.staffCount ?? 0}
                              size="small"
                              color={
                                (classroom.staffCount ?? 0) > 0
                                  ? "secondary"
                                  : "default"
                              }
                              variant={
                                (classroom.staffCount ?? 0) > 0
                                  ? "filled"
                                  : "outlined"
                              }
                              sx={{ fontWeight: 600, minWidth: 36 }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            {canMutate ? (
                              <Box
                                display="flex"
                                justifyContent="center"
                                gap={0.5}
                              >
                                <Tooltip title={t("labels.edit")}>
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      setSelectedItem(classroom);
                                      setOpenEdit(true);
                                    }}
                                  >
                                    <Edit fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title={t("classroom.delete")}>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    aria-label={t("classroom.delete")}
                                    onClick={() => {
                                      setSelectedItem(classroom);
                                      setOpenDelete(true);
                                    }}
                                  >
                                    <DeleteOutline fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            ) : (
                              <Typography variant="body2" color="text.disabled">
                                —
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Divider />
              <TablePaginationLocalizationProvider>
                <TablePagination
                  rowsPerPageOptions={[10, 25, 50]}
                  component="div"
                  count={sorted.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={(_, p) => setPage(p)}
                  onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                />
              </TablePaginationLocalizationProvider>
            </Card>
          )}
        </Container>
      )}

      {canMutate && (
        <Fab
          variant="extended"
          color="primary"
          sx={{
            position: "fixed",
            right: { xs: 16, sm: 24 },
            bottom: { xs: 16, sm: 24 },
            pl: 2.5,
            pr: 3,
            gap: 1,
          }}
          onClick={() => setOpenAdd(true)}
        >
          <Add />
          {t("classroom.add")}
        </Fab>
      )}

      {(openAdd || (openEdit && selectedItem)) && (
        <ClassroomDialog
          open={openAdd || openEdit}
          onClose={handleDialogClose}
          item={openEdit ? selectedItem : undefined}
        />
      )}

      {openDelete && selectedItem && (
        <DeleteClassroomDialog
          open={openDelete}
          onClose={handleDeleteClose}
          item={selectedItem}
        />
      )}
    </>
  );
};

export default ClassroomsPage;
